/**
 * Describe a column… / Describe a condition… from the command search — the
 * screen changes a described expression makes, as pure patches.
 *
 * The English → DSL call itself is `describeExpr` (`api/compile-expr.ts`), the
 * one the column editor's Describe mode already makes; this module only decides
 * what an answer *does* to the screen, so it can be tested without a model.
 *
 * **One patch each, so one undo.** A condition is added to the library and
 * ticked in the same `updateScreener` — two dispatches would be two undo steps,
 * and undoing the second would leave a condition in the library that nobody
 * asked to keep without the filter that was the point of asking.
 *
 * The shapes copy the builder's own paths rather than inventing new ones: a
 * column is what `+ Computed column` appends (`field`, `header`, `computed`
 * with `rollup: "recompute"`), and a condition is what `Save as new` writes,
 * then `applySelection` — the picker's own tick — turns it on beside whatever
 * was already on.
 */

import type { SavedScreenerFilter, ScreenerColumn, ScreenerConfig, ScreenerExpr } from "@qufolio/model";
import { applySelection, selectedFilterIds } from "@qufolio/screener-grid";

import type { ScreenerPatch } from "./workspace-actions";

/** What the command needs from a compiled answer — `CompileSuccess`'s fields. */
export interface Described {
  expression: ScreenerExpr;
  source: string;
  requires?: readonly string[];
}

/** A request as a header or a condition name: one line, short enough for a column. */
export function describedName(request: string, max = 40): string {
  const line = request.trim().replace(/\s+/g, " ");
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line || "Described";
}

/**
 * A field id no column on the screen already has, and not one the server owns —
 * `computedN`, the builder's own spelling, counted up past any taken.
 */
export function freeComputedField(config: ScreenerConfig, serverFields: readonly string[] = []): string {
  const taken = new Set([...(config.columns ?? []).map((c) => c.field), ...serverFields]);
  for (let n = (config.columns ?? []).filter((c) => c.computed).length + 1; ; n++) {
    const field = `computed${n}`;
    if (!taken.has(field)) return field;
  }
}

/** Append a computed column; the patch, and the field it was given. */
export function describedColumnPatch(
  config: ScreenerConfig,
  described: Described,
  request: string,
  serverFields: readonly string[] = [],
): { patch: ScreenerPatch; field: string } {
  const field = freeComputedField(config, serverFields);
  const column: ScreenerColumn = {
    field,
    header: describedName(request),
    computed: {
      expression: described.expression,
      source: described.source,
      ...(described.requires ? { requires: [...described.requires] } : {}),
      rollup: "recompute",
    },
  };
  return { patch: { columns: [...(config.columns ?? []), column] }, field };
}

/**
 * Add a condition to the screen's library **and** tick it, beside what was
 * already ticked — the library and the filter in one patch.
 */
export function describedConditionPatch(
  config: ScreenerConfig,
  described: Described,
  request: string,
  mintId: () => string,
): { patch: ScreenerPatch; id: string } {
  const entry: SavedScreenerFilter = {
    id: mintId(),
    name: describedName(request),
    filter: described.expression,
  };
  const savedFilters = [...(config.savedFilters ?? []), entry];
  const selection = applySelection({ ...config, savedFilters }, [...selectedFilterIds(config), entry.id]);
  return { patch: { savedFilters, ...selection }, id: entry.id };
}

/**
 * Load a condition into the filter editor **unsaved** — [Edit] rather than
 * [Add]. Wrapped in a group, because the builder edits a group and shows an
 * empty one for a bare expression; nothing ticked, because nothing was saved.
 */
export function describedConditionEditPatch(described: Described): ScreenerPatch {
  return {
    filter: { op: "all", children: [described.expression] },
    activeFilterRef: undefined,
    activeFilterRefs: [],
  };
}
